import { defineStore } from "pinia";
import { ServiceTeamItem } from "@/services/entities/ServiceTeams/ServiceTeamItem";
import { getServerUrl } from "@/common/urls";
import { closeToast, showLoading, showSuccess, showToastError } from "@/toastification";
import ServiceTeamsFetcher from "@/services/connectors/serviceTeamsConnector";
import { parseError } from "@/common/util";

const teamsFetcher = new ServiceTeamsFetcher(getServerUrl());

export const useServiceTeamsStore = defineStore("serviceTeams", {
    state: () => ({
        serviceTeam: {
            data: [] as ServiceTeamItem[],
            loader: false,
        },
    }),
    actions: {
        async loadServiceTeams(): Promise<void> {
            try {
                this.serviceTeam.loader = false;
                this.serviceTeam.data = await teamsFetcher.getServiceTeams();
            } catch (e: any) {
                console.error(e);
            } finally {
                this.serviceTeam.loader = true;
            }
        },
        async createServiceTeam(team: ServiceTeamItem): Promise<void> {
            const toast = showLoading("Creating service team...");
            try {
                await teamsFetcher.createServiceTeam(team);
                showSuccess("Service team created successfully.");
                await this.loadServiceTeams();
            } catch (e: any) {
                showToastError("Error creating service team: " + parseError(e));
            } finally {
                closeToast(toast);
            }
        },
        async updateServiceTeam(team: ServiceTeamItem): Promise<void> {
            const toast = showLoading("Updating service team...");
            try {
                await teamsFetcher.updateServiceTeam(team);
                showSuccess("Service team updated successfully.");
                await this.loadServiceTeams();
            } catch (e: any) {
                showToastError("Error updating service team: " + parseError(e));
            } finally {
                closeToast(toast);
            }
        },
        async getServiceTeamById(id: string): Promise<ServiceTeamItem | null> {
            try {
                const team = await teamsFetcher.getServiceTeamById(id);
                return team;
            } catch (e: any) {
                console.error(e);
                return null;
            }
        },
        async deleteServiceTeam(id: string): Promise<void> {
            const toast = showLoading("Deleting service team...");
            try {
                await teamsFetcher.deleteServiceTeam(id);
                showSuccess("Service team deleted successfully.");
                await this.loadServiceTeams();
            } catch (e: any) {
                showToastError("Error deleting service team: " + parseError(e));
            } finally {
                closeToast(toast);
            }
        }
    },
    getters: {
        getServiceTeams(): ServiceTeamItem[] {
            return this.serviceTeam.data;
        },
        isLoaded(): boolean {
            return this.serviceTeam.loader;
        }
    }
});